// SAM-1105 — Weekly Wave 22 oct: covers with a white or almost white border look washed out in the app.
// Looks at the outer ring of the picked file, in the browser, before it is uploaded. A warning, never a block.

/** A pixel counts as white when every channel is at least this (or it is see-through, which shows white). */
const WHITE_MIN = 240;
const ALPHA_MAX = 10;

/**
 * Share of the outer `ring` pixels of an RGBA image that are white or transparent.
 * `data` is canvas ImageData (4 bytes per pixel, row by row).
 */
export const whiteBorderShare = (data: ArrayLike<number>, width: number, height: number, ring = 2): number => {
  if (width <= 0 || height <= 0) return 0;
  let white = 0;
  let total = 0;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (x >= ring && x < width - ring && y >= ring && y < height - ring) continue;
      const i = (y * width + x) * 4;
      total += 1;
      if (data[i + 3] <= ALPHA_MAX || (data[i] >= WHITE_MIN && data[i + 1] >= WHITE_MIN && data[i + 2] >= WHITE_MIN)) white += 1;
    }
  }
  return total ? white / total : 0;
};

/** Mostly white edge: 90% of the ring or more. */
export const WHITE_BORDER_SHARE = 0.9;

/**
 * True when the picked image has a white or almost white border. False when it cannot be read (SVG, a format the
 * browser cannot draw, no canvas): the check must never get in the way of an upload.
 */
export const hasWhiteBorder = async (file: File): Promise<boolean> => {
  if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') return false;
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('unreadable image'));
      el.src = url;
    });
    // 64 px on the long side is plenty for the edge, and keeps the ring at a few hundred pixels.
    const scale = 64 / Math.max(img.naturalWidth, img.naturalHeight, 1);
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    ctx.drawImage(img, 0, 0, w, h);
    return whiteBorderShare(ctx.getImageData(0, 0, w, h).data, w, h) >= WHITE_BORDER_SHARE;
  } catch {
    return false;
  } finally {
    URL.revokeObjectURL(url);
  }
};
